import { useState, useEffect, useRef, useMemo } from "react";
import { Search as SearchIcon, X, TrendingUp, Clock, ArrowRight, User, Tag, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

// Demo data
const profiles = [
  { id: "separ", name: "Separ", slug: "separ", image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop", productCount: 45 },
  { id: "dms", name: "DMS", slug: "dms", image: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=100&h=100&fit=crop", productCount: 32 },
  { id: "majk-spirit", name: "Majk Spirit", slug: "majk-spirit", image: "https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=100&h=100&fit=crop", productCount: 28 },
];

const categories = [
  { id: "1", name: "Tričká", slug: "tricka", productCount: 156 },
  { id: "2", name: "Mikiny", slug: "mikiny", productCount: 89 },
  { id: "3", name: "Čiapky", slug: "ciapky", productCount: 34 },
];

const products = [
  { id: "1", name: "Separ - Classic Hoodie Black", price: 59.99, image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=100&h=100&fit=crop", category: "Mikiny" },
  { id: "2", name: "Separ - Logo Tričko White", price: 29.99, image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=100&h=100&fit=crop", category: "Tričká" },
  { id: "3", name: "DMS - Crew Mikina", price: 54.99, image: "https://images.unsplash.com/photo-1578587018452-892bacefd3f2?w=100&h=100&fit=crop", category: "Mikiny" },
];

const trendingSearches = ["Mikiny", "Limitovaná edícia", "Tričká oversize", "Separ"];

export function SearchOverlay({ isOpen, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredResults = useMemo(() => {
    if (!query.trim()) return { profiles: [], categories: [], products: [] };
    const q = query.toLowerCase();
    return {
      profiles: profiles.filter(p => p.name.toLowerCase().includes(q)).slice(0, 3),
      categories: categories.filter(c => c.name.toLowerCase().includes(q)).slice(0, 4),
      products: products.filter(p => p.name.toLowerCase().includes(q)).slice(0, 4),
    };
  }, [query]);

  const hasResults = filteredResults.profiles.length > 0 || 
                     filteredResults.categories.length > 0 || 
                     filteredResults.products.length > 0;

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (!isOpen) {
          // Parent should handle opening
        }
      }
    };
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("keydown", handleShortcut);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleShortcut);
    };
  }, [isOpen, onClose]);

  const closeAndNavigate = () => {
    onClose();
    setQuery("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 animate-fade-in">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-foreground/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Search Container */}
      <div className="relative mx-auto mt-[10vh] w-full max-w-3xl px-4 animate-scale-in">
        <div className="bg-background rounded-2xl shadow-2xl border border-border overflow-hidden">
          {/* Search Input - Large and Prominent */}
          <div className="relative flex items-center border-b border-border bg-secondary/30">
            <SearchIcon className="absolute left-5 w-6 h-6 text-primary" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hľadať produkty, kategórie, interpreti..."
              className="w-full py-5 pl-14 pr-14 bg-transparent text-xl text-foreground placeholder:text-muted-foreground focus:outline-none font-medium"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-14 p-2 rounded-full hover:bg-secondary transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
            <button
              onClick={onClose}
              className="absolute right-4 p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Content */}
          <div className="max-h-[60vh] overflow-y-auto p-4">
            {query && hasResults ? (
              <div className="space-y-6">
                {/* Profiles */}
                {filteredResults.profiles.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-primary" />
                      <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                        Interpreti
                      </h3>
                    </div>
                    <div className="space-y-1">
                      {filteredResults.profiles.map((profile) => (
                        <Link
                          key={profile.id}
                          to={`/${profile.slug}`}
                          onClick={closeAndNavigate}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-secondary transition-all group"
                        >
                          <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-primary/20 group-hover:ring-primary/50 transition-all">
                            <img src={profile.image} alt={profile.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{profile.name}</p>
                            <p className="text-sm text-muted-foreground">{profile.productCount} produktov</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products */}
                {filteredResults.products.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="w-4 h-4 text-primary" />
                      <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                        Produkty
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {filteredResults.products.map((product) => (
                        <Link
                          key={product.id}
                          to={`/produkt/${product.id}`}
                          onClick={closeAndNavigate}
                          className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 hover:bg-secondary transition-all group"
                        >
                          <div className="w-14 h-14 rounded-lg overflow-hidden">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">{product.name}</p>
                            <p className="text-sm font-bold text-primary">{product.price.toFixed(2)} €</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : query && !hasResults ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
                  <SearchIcon className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-lg font-medium text-foreground mb-1">Žiadne výsledky</p>
                <p className="text-muted-foreground">Skúste iné kľúčové slová</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Trending */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                      Trendy
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {trendingSearches.map((term) => (
                      <button
                        key={term}
                        onClick={() => setQuery(term)}
                        className="px-4 py-2 rounded-full bg-secondary hover:bg-primary hover:text-primary-foreground text-sm font-medium transition-all hover:scale-105"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Categories */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-primary" />
                    <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                      Kategórie
                    </h3>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/kategoria/${cat.slug}`}
                        onClick={closeAndNavigate}
                        className="p-4 rounded-xl bg-secondary/50 hover:bg-secondary text-center transition-all group hover:scale-[1.02]"
                      >
                        <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{cat.name}</p>
                        <p className="text-xs text-muted-foreground">{cat.productCount} produktov</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
